/** One cell of a grid's .xlsx download; a number may carry its Excel number format. */
export type XlsxCell = string | number | boolean | null | { readonly value: number; readonly format: string };

/** A cell's grid formatting, carried when "copy formats" is on. */
export interface CopyCellStyle {
  readonly bold?: boolean | undefined;
  /** A hex fill, e.g. #FDE68A. */
  readonly fill?: string | undefined;
  readonly align?: 'left' | 'center' | 'right' | undefined;
  readonly borderLeft?: { readonly weight: 'thin' | 'medium' | 'thick'; readonly color: string } | undefined;
  readonly borderRight?: { readonly weight: 'thin' | 'medium' | 'thick'; readonly color: string } | undefined;
}

/** One line of the download; `styled` lines (headers, groups, subtotals, totals) go bold on grey. */
export interface XlsxLine {
  readonly cells: readonly XlsxCell[];
  readonly styled?: boolean;
  /** The cells as text for the text formats (csv, md...). */
  readonly texts?: readonly string[] | undefined;
  /** Per-cell formatting, present when "copy formats" is on. */
  readonly styles?: readonly CopyCellStyle[] | undefined;
}

type Exporter = () => XlsxLine[];

const exporters = new Map<string, Exporter>();

/**
 * A mounted grid offers its lines - header first - for its tab's .xlsx download, so the file
 * matches what "copy all" takes. Returns the unregister function.
 */
export function registerGridExport(gridKey: string, exporter: Exporter): () => void {
  exporters.set(gridKey, exporter);
  return () => {
    if (exporters.get(gridKey) === exporter) exporters.delete(gridKey);
  };
}

/** The lines of the grid on screen under this key, or null when none is mounted. */
export function gridExportLines(gridKey: string): XlsxLine[] | null {
  return exporters.get(gridKey)?.() ?? null;
}

/** What the copy menu's Download submenu calls: this sheet in a format, or the whole file. */
export interface GridDownloadActions {
  readonly sheet: (format: 'xlsx' | 'csv' | 'md' | 'tsv' | 'txt') => void | Promise<void>;
  readonly file: (format: 'xlsx' | 'md') => void | Promise<void>;
}
